import type { EmailOtpType, Session, User } from '@supabase/supabase-js'
import supabase from '../config/supabaseConfig'

/** Supabase returns 200 on duplicate signup but leaves identities empty. */
export function isRepeatedSignup(user: User | null | undefined): boolean {
    return !!user && (!user.identities || user.identities.length === 0)
}

export const REPEATED_SIGNUP_MESSAGE =
    'Un compte existe déjà avec cet email. Connectez-vous avec votre mot de passe actuel ou utilisez « Mot de passe oublié ».'

export const INVALID_CREDENTIALS_MESSAGE =
    'Email ou mot de passe incorrect. Si vous venez de vous inscrire, confirmez d\'abord votre email, ou réinitialisez votre mot de passe si vous avez déjà un compte.'

function getUrlParams() {
    const search = new URLSearchParams(window.location.search)
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    return { search, hash }
}

/** Parse Supabase auth tokens from the callback URL and establish a session. */
export async function establishSessionFromUrl(): Promise<{
    session: Session | null
    error: Error | null
    authType?: string
}> {
    const { search, hash } = getUrlParams()

    const code = search.get('code')
    if (code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code)
        return {
            session: data.session,
            error: error ? new Error(error.message) : null,
            authType: search.get('type') ?? hash.get('type') ?? undefined,
        }
    }

    const tokenHash = search.get('token_hash')
    const otpType = search.get('type') as EmailOtpType | null
    if (tokenHash && otpType) {
        const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: otpType })
        return {
            session: data.session,
            error: error ? new Error(error.message) : null,
            authType: otpType,
        }
    }

    const accessToken = hash.get('access_token') || search.get('access_token')
    const refreshToken = hash.get('refresh_token') || search.get('refresh_token')
    const authType = hash.get('type') || search.get('type') || undefined

    if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
        })

        if (!error) {
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search)
        }

        return {
            session: data.session,
            error: error ? new Error(error.message) : null,
            authType,
        }
    }

    const { data: { session }, error } = await supabase.auth.getSession()
    return {
        session,
        error: error ? new Error(error.message) : null,
        authType,
    }
}
